import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Lead } from "@mapmycustomers/shared/types/entity";
import { DuplicateEntity } from "@mapmycustomers/shared/types/entity/Duplicate";
import { Button } from "@mapmycustomers/ui";

import { ReactComponent as TerritoriesIcon } from "@app/assets/map/legends/territoriesDisabled.svg";
import { doesOrganizationSupportTerritories } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getTerritoryCount } from "@app/store/territories";

import styles from "./DuplicateWarningIcon.module.scss";

interface Props {
  disable?: boolean;
  entity: Lead;
  onClick?: (data: { primaryRecord?: Lead; records?: DuplicateEntity[] }) => void;
  territoriesEnabled?: boolean;
  territoryCount: number;
}

const DuplicateWarningIcon = ({
  disable = false,
  entity,
  onClick,
  territoriesEnabled,
  territoryCount,
}: Props) => {
  const intl = useIntl();

  const isOutsideOfTerritories = !entity.isInTerr && territoriesEnabled && territoryCount > 0;
  const isVisible = (!!entity.mmcRecords?.length || isOutsideOfTerritories) && !entity.blackListed;

  const handleShowDuplicates = useCallback(() => {
    onClick?.({
      primaryRecord: entity,
      records: entity.mmcRecords,
    });
  }, [entity, onClick]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Tooltip
        placement="bottom"
        title={intl.formatMessage(
          {
            id: "map.tool.leadFinder.duplicate.tooltip",
            defaultMessage:
              "{outside, select, true {Lead is outside your {count, plural, one {territory} other {territories}}} other {Potential duplicate}}",
            description: "Potential Duplicate button tooltip",
          },
          {
            count: territoryCount,
            outside: isOutsideOfTerritories,
          }
        )}
      >
        <Button
          className={cn(styles.button, { [styles.warning]: !isOutsideOfTerritories })}
          danger={isOutsideOfTerritories}
          iconOnly
          onClick={disable ? undefined : handleShowDuplicates}
          size="large"
        >
          {!isOutsideOfTerritories ? (
            <FontAwesomeIcon className={styles.icon} icon={faExclamationTriangle} />
          ) : (
            <TerritoriesIcon className={styles.icon} />
          )}
        </Button>
      </Tooltip>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  territoriesEnabled: doesOrganizationSupportTerritories(state),
  territoryCount: getTerritoryCount(state),
});

export default connect(mapStateToProps, {})(DuplicateWarningIcon);
